import { Component, OnDestroy, OnInit } from '@angular/core';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { BadgeModule } from 'primeng/badge';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { getAuth } from 'firebase/auth';
import { Router } from '@angular/router';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { MeService } from '../../global-services/me.service';
import { MeModel } from '../../global-data-models/me.model';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { TagModule } from 'primeng/tag';
import { GlobalVersionNumberComponent } from '../global-version-number/global-version-number.component';

@Component({
  selector: 'app-global-sidebar',
  imports: [...COMMON_IMPORTS, RippleModule, StyleClassModule, BadgeModule, OverlayPanelModule, TranslateModule, TagModule, GlobalVersionNumberComponent],
  templateUrl: './global-sidebar.component.html',
  styleUrl: './global-sidebar.component.css',
})
export class GlobalSidebarComponent implements OnInit, OnDestroy {
  me: MeModel | null = null;
  meSubscription: Subscription | null = null;
  showAdminItems = false;
  showOrgAdminItems = false;
  currentOrganisationName = '';
  allOrgs: {
    id: string;
    legalName: string;
    displayName: string;
  }[] = [];
  sidebarVisible = false;
  showStagingBanner = environment.SHOW_STAGING_BANNER;

  constructor(
    private router: Router,
    private message: GlobalMessageService,
    private loading: GlobalLoadingService,
    private meService: MeService,
  ) { }

  ngOnInit() {
    this.subsribeToMeSubscriptions();
  }

  private async subsribeToMeSubscriptions() {
    const firstMe = await this.meService.getMe();
    this.meDidUpdate(firstMe);

    this.meSubscription = this.meService.me$.subscribe((me) => {
      this.meDidUpdate(me);
    });
  }

  private meDidUpdate(newMe: MeModel | null) {
    this.me = newMe;
    this.showAdminItems = MeService.isSystemAdminUser(this.me);
    this.showOrgAdminItems = MeService.isOrgAdminUser(this.me);
    this.currentOrganisationName = MeService.extractCurrentOrgName(this.me) ?? '';
    this.allOrgs = MeService.extractAllOrgs(this.me);
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
  }

  async signoutPressed() {
    try {
      this.loading.show();
      const auth = getAuth();
      await auth.signOut();
      this.message.showSuccess('Goodbye 👋', 'Signed out successfully!');
      await this.router.navigate(['/sign-in']);
    } catch {
    } finally {
      this.loading.hide();
    }
  }

  async onOrgSelected(orgId: string | undefined) {
    if (!orgId || !this.me || orgId === this.me.currentOrganisationId) {
      return;
    }

    try {
      this.loading.show();
      const me = await this.meService.refreshMe();
      await this.meService.changeCurrentOrgId(orgId, me!);
      window.location.href = "/home";
    } catch (err) {
      const errorMessage = (err as any)?.error?.message || (err as any)?.message || 'Unknown error';
      console.error('Error switching organisation:', err);
      this.message.showError('Error', errorMessage);
    } finally {
      this.loading.hide();
    }
  }
}
